import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  purchaseService,
  CreateVendorDto,
  UpdateVendorDto,
  CreatePurchaseOrderDto,
  PurchaseOrder,
  PurchaseOrderQueryParams,
} from '../services/purchase.service';

// Vendor hooks
export const useVendors = (search?: string) => {
  return useQuery({
    queryKey: ['vendors', search],
    queryFn: () => purchaseService.getVendors(search),
  });
};

export const useActiveVendors = (enabled = true) => {
  return useQuery({
    queryKey: ['vendors', 'active'],
    queryFn: () => purchaseService.getActiveVendors(),
    enabled,
  });
};

export const useVendor = (id: string) => {
  return useQuery({
    queryKey: ['vendor', id],
    queryFn: () => purchaseService.getVendor(id),
    enabled: !!id,
  });
};

export const useCreateVendor = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateVendorDto) => purchaseService.createVendor(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
};

export const useUpdateVendor = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateVendorDto }) =>
      purchaseService.updateVendor(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
};

export const useDeleteVendor = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => purchaseService.deleteVendor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
};

// Purchase Order hooks
export const usePurchaseOrders = (status?: string) => {
  return useQuery({
    queryKey: ['purchaseOrders', status],
    queryFn: () => purchaseService.getPurchaseOrders(status),
  });
};

export const usePurchaseOrdersPage = (params: PurchaseOrderQueryParams) => {
  return useQuery({
    queryKey: ['purchaseOrders', 'page', params],
    queryFn: () => purchaseService.getPurchaseOrdersPage(params),
    placeholderData: keepPreviousData,
  });
};

export const usePurchaseOrderSummary = () => {
  return useQuery({
    queryKey: ['purchaseOrders', 'summary'],
    queryFn: () => purchaseService.getPurchaseOrderSummary(),
  });
};

export const usePurchaseOrder = (id: string) => {
  return useQuery({
    queryKey: ['purchaseOrder', id],
    queryFn: () => purchaseService.getPurchaseOrder(id),
    enabled: !!id,
  });
};

export const useCreatePurchaseOrder = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreatePurchaseOrderDto) => purchaseService.createPurchaseOrder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] });
    },
  });
};

export const useUpdatePurchaseOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreatePurchaseOrderDto> }) =>
      purchaseService.updatePurchaseOrder(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] });
    },
  });
};

export const useAmendPurchaseOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreatePurchaseOrderDto> & { reason: string } }) =>
      purchaseService.amendPurchaseOrder(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] });
      queryClient.invalidateQueries({ queryKey: ['goodsReceipts'] });
    },
  });
};

export const usePurchaseOrderAmendments = (id: string | undefined) => {
  return useQuery({
    queryKey: ['purchaseOrders', 'amendments', id],
    queryFn: () => purchaseService.getAmendments(id as string),
    enabled: !!id,
  });
};

export const useDeletePurchaseOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => purchaseService.deletePurchaseOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] });
    },
  });
};

export const useUpdatePurchaseOrderStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: PurchaseOrder['status'] }) =>
      purchaseService.updatePurchaseOrderStatus(id, status),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] });
      // Receiving an order changes product stock
      if (variables.status === 'received') {
        queryClient.invalidateQueries({ queryKey: ['products'] });
      }
    },
  });
};